import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryService } from "@/api/services/query-service";
import { useCheckoutStore } from "../store/checkout-store";
import { useToast } from "@/features/toast";
import { useTranslation } from "react-i18next";
import type {
  AddAddressPayload,
  Address,
  UpdateAddressPayload,
} from "@/api/models/api-types";

export const useCheckoutAddresses = () => {
  const { t } = useTranslation();
  const { success, error } = useToast();
  const queryClient = useQueryClient();

  // Mevcut API hooks - address endpoint'leri zaten var
  const {
    data: addressesData,
    isLoading,
    error: addressesError,
  } = useQuery({
    queryKey: ["addresses"],
    queryFn: () => queryService.getAddresses(),
  });

  const addresses = addressesData?.data || [];

  // CRUD Mutations - mevcut endpoint'leri kullan
  const addAddressMutation = useMutation({
    mutationFn: (payload: AddAddressPayload) =>
      queryService.addAddress(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["addresses"] });
      success(t("checkout.addressAddedSuccess"));
      closeAddressModal();
    },
    onError: (apiError: any) => {
      error(
        apiError?.message || t("checkout.addressAddedError") || "Error adding address"
      );
    },
  });

  const updateAddressMutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateAddressPayload;
    }) => queryService.updateAddress(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["addresses"] });
      success(t("checkout.addressUpdatedSuccess"));
      closeAddressModal();
    },
    onError: (apiError: any) => {
      error(
        apiError?.message || t("checkout.addressUpdatedError") || "Error updating address"
      );
    },
  });

  const deleteAddressMutation = useMutation({
    mutationFn: (id: string) => queryService.deleteAddress(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["addresses"] });
      success(t("checkout.addressDeletedSuccess"));
      closeDeleteAddressDialog();
    },
    onError: (apiError: any) => {
      error(
        apiError?.message || t("checkout.addressDeletedError") || "Error deleting address"
      );
    },
  });

  // Store Integration
  const {
    selectedDeliveryAddressId,
    selectedBillingAddressId,
    useSameAddressForBilling,
    setDeliveryAddress,
    setBillingAddress,
    isAddressModalOpen,
    editingAddressId,
    closeAddressModal,
    openDeleteAddressDialog,
    closeDeleteAddressDialog,
    openAddressModal,
  } = useCheckoutStore();

  const extractSavedAddress = (result: unknown): Address | null => {
    if (!result || typeof result !== "object") return null;
    const payload = result as { data?: Address; _id?: string };
    if (payload.data?._id) return payload.data;
    if (payload._id) return payload as Address;
    return null;
  };

  // Kayıtlı adres varken seçim yoksa (veya silinmiş id kaldıysa) varsayılanı seç
  useEffect(() => {
    if (isLoading || addresses.length === 0) return;

    const deliveryExists = addresses.some(
      (address) => address._id === selectedDeliveryAddressId
    );
    if (!selectedDeliveryAddressId || !deliveryExists) {
      const fallback =
        addresses.find((address) => address.isDefault) || addresses[0];
      setDeliveryAddress(fallback._id);
    }

    if (!useSameAddressForBilling) {
      const billingExists = addresses.some(
        (address) => address._id === selectedBillingAddressId
      );
      if (!selectedBillingAddressId || !billingExists) {
        const fallback =
          addresses.find((address) => address.isDefault) || addresses[0];
        setBillingAddress(fallback._id);
      }
    }
  }, [
    isLoading,
    addresses,
    selectedDeliveryAddressId,
    selectedBillingAddressId,
    useSameAddressForBilling,
    setDeliveryAddress,
    setBillingAddress,
  ]);

  // Business Logic
  const handleSaveAddress = async (
    data: AddAddressPayload | UpdateAddressPayload,
    addressId?: string,
    selectAs: "delivery" | "billing" = "delivery"
  ) => {
    const idToEdit = addressId || editingAddressId;
    if (idToEdit) {
      const result = await updateAddressMutation.mutateAsync({
        id: idToEdit,
        payload: data as UpdateAddressPayload,
      });
      await queryClient.refetchQueries({ queryKey: ["addresses"] });
      return extractSavedAddress(result);
    }

    const result = await addAddressMutation.mutateAsync(
      data as AddAddressPayload
    );
    await queryClient.refetchQueries({ queryKey: ["addresses"] });
    const created = extractSavedAddress(result);
    if (created?._id) {
      if (selectAs === "billing") {
        setBillingAddress(created._id);
      } else {
        setDeliveryAddress(created._id);
      }
    }
    return created;
  };

  const handleDeleteAddress = async (id: string) => {
    await deleteAddressMutation.mutateAsync(id);

    // Silinen address seçiliyse, null yap
    if (selectedDeliveryAddressId === id) {
      setDeliveryAddress(null);
    }
    if (selectedBillingAddressId === id) {
      setBillingAddress(null);
    }
  };

  return {
    // Data
    addresses,
    isLoading,
    addressesError,

    // Selected addresses
    selectedDeliveryAddressId,
    selectedBillingAddressId,

    // Modal states
    isAddressModalOpen,
    editingAddressId,

    // Actions
    saveAddress: handleSaveAddress,
    deleteAddress: handleDeleteAddress,
    setDeliveryAddress,
    setBillingAddress,
    openAddressModal,
    closeAddressModal,
    openDeleteAddressDialog,
    closeDeleteAddressDialog,

    // Loading states
    isSaving:
      addAddressMutation.isPending || updateAddressMutation.isPending,
    isDeleting: deleteAddressMutation.isPending,
  };
};

